import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { CapHeaderService, CapHeaderUserInfoData, CapHeaderViewPermissions } from '@cap/cap-ui';
import { GetUserPermissions } from '@modules/store/user-store/actions';
import { getUserState$ } from '@modules/store/user-store/selectors';
import { Store } from '@ngrx/store';
import { AuthGuardService, SsoSuccess } from 'auth';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { EnvironmentTypes } from 'src/app/common/models/environment-types.enum';
import { EnvironmentService } from 'src/app/common/services/environment.service';
import { AppState } from '../app.states';
import { CookieService } from 'ngx-cookie-service';
import { AppConfigService } from 'app-config';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    public permissionObj;
    constructor(
        private authGuardService: AuthGuardService,
        private store$: Store<AppState>,
        private environmentService: EnvironmentService,
        private capHeaderService: CapHeaderService,
        public router: Router,
        private cookieService: CookieService
    ) { }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {

        const cookie = this.getCapOauthCookie();
        if (cookie) {
            localStorage.setItem('token', cookie);
        }

        // *** Start *** Check if token present then prevent abck to ask for login
        let customToken = localStorage.getItem('token');
        if (customToken && customToken != "") {
            this.store$.dispatch(
                new SsoSuccess({
                    token: customToken,
                })
            );

        }
        // *** End *** Check if token present then prevent abck to ask for login

        // TO SET ACCESS TOKEN IN STORE AND REMOVE ACCESS TOKEN FROM URL.
        const actualUrl = window.location.href;
        if (actualUrl.indexOf('access_token') > -1) {
            const token = actualUrl.substring(actualUrl.indexOf('access_token') + 13);
            this.store$.dispatch(new SsoSuccess({ token: token }));
            const url = actualUrl.split('?access_token')[0];
            setTimeout(() => {
                window.history.pushState({}, 'PT Image Mapping UI', url);
                if (!token || token == undefined || token == null || token == 'null') {
                    window.location.reload();
                }
            }, 1000);
        }

        if (next.routeConfig.path !== "home" && next.routeConfig.path !== "kit/:kitNumber/images") {
            return this.authGuardService.canActivate(next, state).pipe(
                filter((isAuthenticated) => !!isAuthenticated),
                tap(() => {
                    // Once the user is authenticated, fetch their permissions.
                    const permission = this.store$.select('userInfoState');
                    permission.pipe(take(1)).subscribe((state_p: any) => {
                        this.permissionObj = state_p;
                        if (!('isCommitteeMember' in this.permissionObj)) {
                            this.permissionObj = this.store$.dispatch(new GetUserPermissions());
                        }
                    });

                    return this.permissionObj;
                }),
                take(1),
                switchMap(() => this.waitForUserInformationToLoad$()),
                filter((hasUserInformationLoaded) => !!hasUserInformationLoaded),
                map(() => {
                    return true;
                })
            );
        } else {
            return this.authGuardService.canActivate(next, state).pipe(
                filter((isAuthenticated) => isAuthenticated)
            );
        }
    }
    // to get the user permission and store the data to redux store.
    waitForUserInformationToLoad$(): Observable<boolean> {
        return this.store$.select(getUserState$).pipe(
            filter(
                (userState) =>
                    !!userState &&
                    !!userState.userMemberId &&
                    !!userState.loaded
            ),
            tap((userState: any) => {

                const viewPermissions = userState.elssHeaderItemViewPermissions as CapHeaderViewPermissions[];
                const capHeaderUserInfoData: CapHeaderUserInfoData = {
                    ...userState,
                    viewPermissions
                };
                this.capHeaderService.setUserInfo(capHeaderUserInfoData);
                if (!userState.isCAPStaff && !userState.isCommitteeMember) {
                    this.router.navigateByUrl('unauthorized');
                }
            }),
            map(() => true)
        );
    }

    getCapOauthCookie = () => {
        const capOauthCookieKey = AppConfigService.settings.apiServer.authGuard.capoauth;

        return this.cookieService.get(capOauthCookieKey) || null;
    }
}
